import type { SellerDecision, SellerVoteChoice } from '../api/types';
import { ConfirmDialog } from './confirm-dialog';

/**
 * The "Are you sure?" step in front of EVERY LOI / exclusivity decision click
 * (owner ruling 2026-10-22): an owner's Approve or Decline vote, and what the
 * intermediary enters (accept, counter, decline, extension). The dialog names the
 * buyer and says in plain words what the click does — and, for the intermediary,
 * that it is only a PROPOSAL the owners must carry. One vocabulary for the admin
 * app and the seller portal.
 */

export interface DecisionCopy {
  /** The question, naming the buyer: "Approve accepting Northwind Partners’ LOI?" */
  question: string;
  /** What happens, in plain words. */
  consequence: string;
  /** The confirm button. */
  confirmLabel: string;
}

/** The possessive of a name, with the curly apostrophe: "Northwind Partners’", "Harbor Capital’s". */
function possessive(name: string): string {
  return /s$/i.test(name) ? `${name}’` : `${name}’s`;
}

/** "Northwind Partners’ LOI". */
function loiOf(buyer: string): string {
  return `${possessive(buyer)} LOI`;
}

type Responding = 'accept' | 'counter' | 'reject' | 'extend';

const MAJORITY = 'owners holding more than 50%';

/** What an owner's Approve / Decline vote on an open decision does. */
export function voteCopy(
  d: Pick<
    SellerDecision,
    'action' | 'buyerEmail' | 'lengthDays' | 'startDate' | 'additionalDays'
  >,
  vote: SellerVoteChoice,
): DecisionCopy {
  const buyer = d.buyerEmail ?? 'the buyer';
  const days = d.additionalDays ?? null;
  if (vote === 'approve') {
    switch (d.action) {
      case 'accept':
        return {
          question: `Approve accepting ${loiOf(buyer)}?`,
          consequence:
            `Once ${MAJORITY} approve, the LOI is accepted and the exclusivity period starts` +
            (d.lengthDays != null ? ` (${d.lengthDays} days)` : '') +
            '. Every other buyer’s data room access is paused until it ends.',
          confirmLabel: 'Yes, approve',
        };
      case 'counter':
        return {
          question: `Approve countering ${loiOf(buyer)}?`,
          consequence: `Once ${MAJORITY} approve, the LOI is recorded as countered and the buyer can submit a new one.`,
          confirmLabel: 'Yes, approve',
        };
      case 'reject':
        return {
          question: `Approve declining ${loiOf(buyer)}?`,
          consequence: `Once ${MAJORITY} approve, the LOI is declined and the buyer can submit a new one.`,
          confirmLabel: 'Yes, approve',
        };
      case 'extend':
        return {
          question: `Approve extending ${possessive(buyer)} exclusivity${days != null ? ` by ${days} days` : ''}?`,
          consequence: `Once ${MAJORITY} approve, the exclusivity period is extended${days != null ? ` by ${days} days` : ''} and every other buyer stays paused until the new end date.`,
          confirmLabel: 'Yes, approve',
        };
    }
  }
  switch (d.action) {
    case 'accept':
      return {
        question: `Decline accepting ${loiOf(buyer)}?`,
        consequence:
          'If owners holding 50% or more decline, the acceptance is dropped and the LOI is open for a response again.',
        confirmLabel: 'Yes, decline',
      };
    case 'counter':
      return {
        question: `Decline countering ${loiOf(buyer)}?`,
        consequence:
          'If owners holding 50% or more decline, the proposal is dropped and nothing is recorded; the LOI stays open for a response.',
        confirmLabel: 'Yes, decline',
      };
    case 'reject':
      return {
        question: `Vote against declining ${loiOf(buyer)}?`,
        consequence:
          'If owners holding 50% or more vote against it, the proposal is dropped and nothing is recorded; the LOI stays open for a response.',
        confirmLabel: 'Yes, vote against',
      };
    case 'extend':
      return {
        question: `Decline extending ${possessive(buyer)} exclusivity${days != null ? ` by ${days} days` : ''}?`,
        consequence:
          'If owners holding 50% or more decline, the extension is dropped and the exclusivity period keeps its current end date.',
        confirmLabel: 'Yes, decline',
      };
  }
}

/**
 * What entering a response does: the INTERMEDIARY's is only a proposal; an OWNER's
 * is their own Approve in the owners' vote.
 */
export function responseCopy(
  action: Exclude<Responding, 'extend'>,
  buyer: string,
  viewer: 'intermediary' | 'owner',
): DecisionCopy {
  const verb = action === 'accept' ? 'accepting' : action === 'counter' ? 'countering' : 'declining';
  const what =
    action === 'accept'
      ? 'accepted and the exclusivity period starts'
      : action === 'counter'
        ? 'recorded as countered'
        : 'declined';
  if (viewer === 'intermediary') {
    return {
      question: `Propose ${verb} ${loiOf(buyer)}?`,
      consequence:
        `This is a proposal, not a decision. Nothing happens until ${MAJORITY} approve it; the ` +
        `owners are asked by email and in their portal. Once they do, the LOI is ${what}.`,
      confirmLabel: 'Yes, send to the owners',
    };
  }
  return {
    question: `${action === 'accept' ? 'Accept' : action === 'counter' ? 'Counter' : 'Decline'} ${loiOf(buyer)}?`,
    consequence:
      `Your response is your vote. With one owner at 100% the LOI is ${what} at once; otherwise ` +
      `it is ${what} once ${MAJORITY} approve, and the other owners are asked to vote.`,
    confirmLabel: 'Yes, continue',
  };
}

/** What entering an exclusivity extension does (a proposal for the intermediary). */
export function extensionCopy(
  buyer: string,
  days: number,
  viewer: 'intermediary' | 'owner',
): DecisionCopy {
  return viewer === 'intermediary'
    ? {
        question: `Propose extending ${possessive(buyer)} exclusivity by ${days} days?`,
        consequence:
          `This is a proposal, not a decision. Nothing changes until ${MAJORITY} approve it; ` +
          'the owners are asked by email and in their portal.',
        confirmLabel: 'Yes, send to the owners',
      }
    : {
        question: `Extend ${possessive(buyer)} exclusivity by ${days} days?`,
        consequence:
          `Your request is your vote. With one owner at 100% the period is extended at once; ` +
          `otherwise it is extended once ${MAJORITY} approve, and the other owners are asked to vote.`,
        confirmLabel: 'Yes, extend',
      };
}

/** The "Are you sure?" alert dialog for one decision click. */
export function DecisionConfirmDialog({
  copy,
  pending = false,
  danger = false,
  onConfirm,
  onCancel,
}: {
  copy: DecisionCopy;
  pending?: boolean;
  /** A red confirm (declines and refusals). */
  danger?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <ConfirmDialog
      title="Are you sure?"
      confirmLabel={copy.confirmLabel}
      danger={danger}
      pending={pending}
      onCancel={onCancel}
      onConfirm={onConfirm}
    >
      <p className="font-medium text-foreground" data-testid="decision-question">
        {copy.question}
      </p>
      <p data-testid="decision-consequence">{copy.consequence}</p>
    </ConfirmDialog>
  );
}
